import React, { useState } from 'react';
import AdminNavBar from './AdminNavBar';
import './AdminAlertsPage.css';

// ── Types ──────────────────────────────────────────────────────────────────

type MetricKey =
  | 'error_rate_1h'
  | 'latency_avg_ms'
  | 'failed_jobs'
  | 'ai_cost_usd_day';
type Operator = '>' | '<';

interface AlertRule {
  id: string;
  name: string;
  metric: MetricKey;
  operator: Operator;
  threshold: number;
  enabled: boolean;
}

const METRIC_LABELS: Record<MetricKey, string> = {
  error_rate_1h: 'Error count (last 1h)',
  latency_avg_ms: 'Avg latency (ms)',
  failed_jobs: 'Failed jobs count',
  ai_cost_usd_day: 'AI cost today (USD)'
};

const STORAGE_KEY = 'admin_alert_rules_v1';

function loadRules(): AlertRule[] {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '[]') as AlertRule[];
  } catch {
    return [];
  }
}

function saveRules(rules: AlertRule[]): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(rules));
}

function genId(): string {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
}

// ── Blank rule form ────────────────────────────────────────────────────────

interface RuleForm {
  name: string;
  metric: MetricKey;
  operator: Operator;
  threshold: string;
}

const BLANK_FORM: RuleForm = {
  name: '',
  metric: 'error_rate_1h',
  operator: '>',
  threshold: ''
};

// ── Component ──────────────────────────────────────────────────────────────

export default function AdminAlertsPage() {
  const [rules, setRules] = useState<AlertRule[]>(loadRules);
  const [form, setForm] = useState<RuleForm>(BLANK_FORM);
  const [editId, setEditId] = useState<string | null>(null);
  const [formError, setFormError] = useState('');

  const persist = (next: AlertRule[]) => {
    setRules(next);
    saveRules(next);
  };

  const handleToggle = (id: string) => {
    persist(
      rules.map((r) => (r.id === id ? { ...r, enabled: !r.enabled } : r))
    );
  };

  const handleDelete = (id: string) => {
    persist(rules.filter((r) => r.id !== id));
    if (editId === id) {
      setEditId(null);
      setForm(BLANK_FORM);
    }
  };

  const handleEdit = (rule: AlertRule) => {
    setEditId(rule.id);
    setForm({
      name: rule.name,
      metric: rule.metric,
      operator: rule.operator,
      threshold: String(rule.threshold)
    });
    setFormError('');
  };

  const handleCancelEdit = () => {
    setEditId(null);
    setForm(BLANK_FORM);
    setFormError('');
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const threshold = parseFloat(form.threshold);
    if (!form.name.trim()) {
      setFormError('Rule name is required.');
      return;
    }
    if (!Number.isFinite(threshold)) {
      setFormError('Threshold must be a valid number.');
      return;
    }
    setFormError('');

    if (editId) {
      persist(
        rules.map((r) =>
          r.id === editId
            ? {
                ...r,
                name: form.name.trim(),
                metric: form.metric,
                operator: form.operator,
                threshold
              }
            : r
        )
      );
      setEditId(null);
    } else {
      persist([
        ...rules,
        {
          id: genId(),
          name: form.name.trim(),
          metric: form.metric,
          operator: form.operator,
          threshold,
          enabled: true
        }
      ]);
    }
    setForm(BLANK_FORM);
  };

  return (
    <div className='container-fluid alrt-page'>
      <AdminNavBar />

      <div className='alrt-toolbar'>
        <h4 className='alrt-title'>Alert Rules</h4>
      </div>

      <p className='alrt-description'>
        Define threshold-based alert rules. Rules are stored locally in your
        browser. They serve as a reference checklist for monitoring thresholds —
        webhook delivery is not yet implemented.
      </p>

      {/* ── Rule list ───────────────────────────────────────────────────── */}
      {rules.length === 0 ? (
        <div className='alrt-empty'>No rules defined yet. Add one below.</div>
      ) : (
        <table className='table table-sm alrt-table'>
          <thead>
            <tr>
              <th>Enabled</th>
              <th>Name</th>
              <th>Metric</th>
              <th>Condition</th>
              <th>Threshold</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rules.map((rule) => (
              <tr
                key={rule.id}
                className={rule.enabled ? '' : 'alrt-row--disabled'}
              >
                <td>
                  <div className='form-check form-switch mb-0'>
                    <input
                      className='form-check-input'
                      type='checkbox'
                      checked={rule.enabled}
                      onChange={() => handleToggle(rule.id)}
                      id={`toggle-${rule.id}`}
                    />
                  </div>
                </td>
                <td className='alrt-name-cell'>{rule.name}</td>
                <td className='alrt-metric-cell'>
                  {METRIC_LABELS[rule.metric]}
                </td>
                <td>{rule.operator}</td>
                <td>{rule.threshold}</td>
                <td>
                  <button
                    className='btn btn-xs btn-outline-secondary me-1'
                    onClick={() => handleEdit(rule)}
                  >
                    Edit
                  </button>
                  <button
                    className='btn btn-xs btn-outline-danger'
                    onClick={() => handleDelete(rule.id)}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* ── Add / edit form ─────────────────────────────────────────────── */}
      <div className='alrt-form-card'>
        <h6 className='alrt-form-title'>{editId ? 'Edit Rule' : 'Add Rule'}</h6>
        <form
          onSubmit={handleSubmit}
          noValidate
        >
          <div className='alrt-form-row'>
            <div className='alrt-form-group'>
              <label className='form-label alrt-label'>Rule Name</label>
              <input
                className='form-control form-control-sm'
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder='e.g. High error rate'
              />
            </div>

            <div className='alrt-form-group'>
              <label className='form-label alrt-label'>Metric</label>
              <select
                className='form-select form-select-sm'
                value={form.metric}
                onChange={(e) =>
                  setForm({ ...form, metric: e.target.value as MetricKey })
                }
              >
                {(Object.entries(METRIC_LABELS) as [MetricKey, string][]).map(
                  ([k, v]) => (
                    <option
                      key={k}
                      value={k}
                    >
                      {v}
                    </option>
                  )
                )}
              </select>
            </div>

            <div className='alrt-form-group alrt-form-group--sm'>
              <label className='form-label alrt-label'>Operator</label>
              <select
                className='form-select form-select-sm'
                value={form.operator}
                onChange={(e) =>
                  setForm({ ...form, operator: e.target.value as Operator })
                }
              >
                <option value='>'>{'>'}</option>
                <option value='<'>{'<'}</option>
              </select>
            </div>

            <div className='alrt-form-group alrt-form-group--sm'>
              <label className='form-label alrt-label'>Threshold</label>
              <input
                className='form-control form-control-sm'
                type='number'
                step='any'
                value={form.threshold}
                onChange={(e) =>
                  setForm({ ...form, threshold: e.target.value })
                }
                placeholder='0'
              />
            </div>
          </div>

          {formError && (
            <div className='alert alert-danger py-1 px-2 alrt-form-error'>
              {formError}
            </div>
          )}

          <div className='alrt-form-actions'>
            <button
              type='submit'
              className='btn btn-sm btn-primary'
            >
              {editId ? 'Save Changes' : 'Add Rule'}
            </button>
            {editId && (
              <button
                type='button'
                className='btn btn-sm btn-outline-secondary'
                onClick={handleCancelEdit}
              >
                Cancel
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
